import WarehouseMap from "../../../classes/WarehouseMap";
import { sortBy } from "lodash";

// Warehouse map getters — zone lists, bin zones, and default warehouse.
// The warehouseMaps collection is the only source of truth; a WarehouseMap's
// zone labels are parsed from its SVG (see classes/WarehouseMap).

// Active, non-archived maps sorted by `order` — the shared base for every
// location getter below.
export const warehouseMapList = (state) => {
  // State normally holds WarehouseMap instances (the Firestore converter);
  // reuse them so zone-label parsing stays memoised per instance. Plain
  // objects (test fixtures) are wrapped.
  const maps = Object.values(state.warehouseMaps || {}).map((entry) =>
    entry instanceof WarehouseMap
      ? entry
      : new WarehouseMap(entry.id, entry.data || {})
  );
  return sortBy(
    maps.filter((map) => map.data.active && !map.data.archived),
    (map) => map.data.order
  );
};

// { warehouseName: [zoneLabels] }.
export const warehouseLocations = (state) => {
  const result = {};
  warehouseMapList(state).forEach((map) => {
    result[map.id] = map.zoneLabels;
  });
  return result;
};

// Flattened, deduplicated zone labels across all active maps.
export const warehouseZones = (state) => {
  return [...new Set(Object.values(warehouseLocations(state)).flat())];
};

// { warehouseName: svgString } for the WarehouseMap renderer. All maps are
// included (active or not) so admins can preview drafts/inactive maps.
export const warehouseMapSvgs = (state) => {
  const result = {};
  Object.values(state.warehouseMaps || {}).forEach((entry) => {
    if (entry?.data?.svg) {
      result[entry.id] = entry.data.svg;
    }
  });
  return result;
};

// `warehouse:zone` locations whose zones carry numbered bins — the same
// shape as the old WAREHOUSE_LOCATIONS_WITH_BINS constant.
export const warehouseLocationsWithBins = (state) => {
  return warehouseMapList(state).flatMap((map) => map.binZoneLocations);
};

// The flagged default when it's active, else the first active map by order,
// else null when no warehouse maps exist.
export const defaultWarehouse = (state) => {
  const maps = warehouseMapList(state);
  if (!maps.length) return null;
  const flagged = maps.find((map) => map.data.isDefault);
  return (flagged || maps[0]).id;
};

// (warehouse) => LOCATION_INFO key for that warehouse's map, or null.
export const warehouseLocationId = (state) => (warehouse) => {
  const map = Object.values(state.warehouseMaps || {}).find(
    (entry) => entry.id === warehouse
  );
  return map?.data?.locationId || null;
};

// (locationId) => [warehouseIds] — active, non-archived maps assigned to
// this physical location, in `order`.
export const warehousesForLocation = (state) => (locationId) => {
  return warehouseMapList(state)
    .filter((map) => map.data.locationId === locationId)
    .map((map) => map.id);
};
